import { Text, tokens } from '@hotjar/hotsauce';
import styled from '@emotion/styled';
import { useProductCatalogRepo } from '@hotjar/product-catalog';
import React from 'react';
import { useTranslationWithPrefix } from '@hotjar/translations';
import type { SiteItem } from '~/subscription-management/features/service-resource-list/hooks/useServiceResourceList';
import type {
  BillingPeriod,
  ProductAllowance,
} from '~/subscription-management/api/common/model/subscription';
import type { Currency } from '~/subscription-management/api/common/model/customer';
import { PlanName } from './PlanName';
import { updatedProductsForSite } from '../utils';
import { prettifyPriceWithCurrencySymbol } from '~/subscription-management/utils/cost/prettify-price';
import type { DiscountState } from '~/subscription-management/utils/discounts';
import { Discount } from '../../../../../components/discount/Discount';

const S = {
  Grid: styled.div`
    display: grid;
    grid-template-columns: min-content 1fr min-content;
    row-gap: ${tokens.spacer.x1};
    column-gap: ${tokens.spacer.x2};
  `,
  SiteSection: styled.div`
    grid-column: 1 / 4;
    display: flex;
    flex-direction: column;
    row-gap: ${tokens.spacer.x0pt5};
    padding-bottom: ${tokens.spacer.x2};
    margin-bottom: ${tokens.spacer.x1};
    border-bottom: 1px dashed ${tokens.ALIAS_COLORS.ui_border};
  `,
  CapitalizedText: styled(Text)`
    text-transform: capitalize;
  `,
  Price: styled(Text)`
    text-align: right;
  `,
  Discount: styled(Discount)`
    grid-column: 1 / 4;
  `,
  ProductDiscount: styled(Discount)`
    grid-column: 1 / 4;
    margin-top: -6px; // position 2px below previous row in grid with gap of spacer.x1
  `,
};

interface Props {
  site: SiteItem;
  updatedProducts: ProductAllowance[];
  billingPeriod: BillingPeriod;
  currency: Currency;
  showCosts: boolean;
  className?: string;
  bundleDiscount: DiscountState;
  legacyDiscount: DiscountState;
  loading: boolean;
}

export function SiteCostChanges({
  site,
  updatedProducts,
  billingPeriod,
  currency,
  showCosts,
  className,
  bundleDiscount,
  legacyDiscount,
  loading,
}: Props): JSX.Element {
  const [t, tSubscription] = useTranslationWithPrefix([
    'checkout:product-information',
    'checkout:subscriptions',
  ]);

  const siteCurrentProducts = site.products.map(
    (product) => product.renewWith ?? product.effectiveNow,
  );

  const { getProductBySku } = useProductCatalogRepo({
    billingPeriod,
    currency,
  });

  const siteUpdatedProducts = updatedProductsForSite(site, updatedProducts).map((update) =>
    getProductBySku(update.sku),
  );

  return (
    <S.Grid className={className}>
      <S.SiteSection>
        <Text textType="subtitle2">{site.name || tSubscription('site-name-is-not-available')}</Text>
        {bundleDiscount && !loading && <S.Discount discount="bundle" state={bundleDiscount} />}
      </S.SiteSection>

      {siteUpdatedProducts.map((productInfo) => {
        if (!productInfo) {
          return null;
        }

        const previousProduct = siteCurrentProducts.find(
          (info) => info?.product.id === productInfo.product.id,
        );

        const previousProductName =
          site.isTwoSkusActionRequired && productInfo.product.name === 'ask' ? (
            t('plan.complimentary')
          ) : (
            <PlanName productInfo={previousProduct} />
          );

        if (productInfo.allowance.sku === previousProduct?.allowance.sku) {
          return null;
        }

        return (
          <React.Fragment key={productInfo.product.id}>
            <Text textType="subtitle2">{t(`product.${productInfo?.product.name}`)}</Text>
            <S.CapitalizedText textColor="text_light">
              {previousProductName} → <PlanName productInfo={productInfo} />
            </S.CapitalizedText>
            <S.Price>
              {showCosts && (
                <>{prettifyPriceWithCurrencySymbol(currency)(productInfo.allowance.price)}</>
              )}
            </S.Price>
            {legacyDiscount !== null && productInfo.product.name === 'observe' && !loading && (
              <S.ProductDiscount
                discount="legacy"
                state={legacyDiscount}
                useDefaultColor={legacyDiscount === 'applied'}
                noIcon
              />
            )}
          </React.Fragment>
        );
      })}
    </S.Grid>
  );
}
